import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  DeliveryResourceSaveResult,
  DeliveryResourceStatus,
  DeliveryRouteListQuery,
  DeliveryRouteRow,
  DeliveryRouteSaveRequest,
  DriverListQuery,
  DriverRow,
  DriverSaveRequest,
  VehicleListQuery,
  VehicleRow,
  VehicleSaveRequest
} from '../types/deliveryResource';
import type { PageResult } from '../types/result';

const VEHICLE = '/wl/vehicle';
const DRIVER = '/wl/driver';
const ROUTE = '/wl/delivery-route';

/** 配送资源主数据（车辆 / 司机 / 线路）：查询开放，维护需 `wl:delivery:manage`。 */
export function createDeliveryResourceApi(http: HttpClient) {
  return {
    listVehicles(query: VehicleListQuery = {}): Promise<PageResult<VehicleRow>> {
      return http.request<PageResult<VehicleRow>>({ path: VEHICLE, query: toQuery(query) });
    },

    /** 车牌号人工录入、同租户唯一；`id` 为空即新建。 */
    saveVehicle(body: VehicleSaveRequest): Promise<DeliveryResourceSaveResult> {
      return http.request<DeliveryResourceSaveResult>({ path: VEHICLE, method: 'POST', body });
    },

    setVehicleStatus(id: Id, status: DeliveryResourceStatus): Promise<DeliveryResourceSaveResult> {
      return http.request<DeliveryResourceSaveResult>({
        path: `${VEHICLE}/${id}/status`,
        method: 'POST',
        body: { status }
      });
    },

    listDrivers(query: DriverListQuery = {}): Promise<PageResult<DriverRow>> {
      return http.request<PageResult<DriverRow>>({ path: DRIVER, query: toQuery(query) });
    },

    /** 司机编码由服务端生成（`DRV+3 位序号`）。 */
    saveDriver(body: DriverSaveRequest): Promise<DeliveryResourceSaveResult> {
      return http.request<DeliveryResourceSaveResult>({ path: DRIVER, method: 'POST', body });
    },

    setDriverStatus(id: Id, status: DeliveryResourceStatus): Promise<DeliveryResourceSaveResult> {
      return http.request<DeliveryResourceSaveResult>({
        path: `${DRIVER}/${id}/status`,
        method: 'POST',
        body: { status }
      });
    },

    listRoutes(query: DeliveryRouteListQuery = {}): Promise<PageResult<DeliveryRouteRow>> {
      return http.request<PageResult<DeliveryRouteRow>>({ path: ROUTE, query: toQuery(query) });
    },

    /** 线路编码由服务端生成（`RT+3 位序号`）；司机/车辆须属本租户。 */
    saveRoute(body: DeliveryRouteSaveRequest): Promise<DeliveryResourceSaveResult> {
      return http.request<DeliveryResourceSaveResult>({ path: ROUTE, method: 'POST', body });
    },

    setRouteStatus(id: Id, status: DeliveryResourceStatus): Promise<DeliveryResourceSaveResult> {
      return http.request<DeliveryResourceSaveResult>({
        path: `${ROUTE}/${id}/status`,
        method: 'POST',
        body: { status }
      });
    }
  };
}

export type DeliveryResourceApi = ReturnType<typeof createDeliveryResourceApi>;
